import React, { useState } from "react";
import { debuggerService } from "../../services/DebuggerService";
import { CompareRequestParams } from "../../types/chatServiceTypes";
import { useAuth } from "../../providers/AuthContext";
import LoadingSpinner from "../LoadingSpinner";

interface SingleBenchmarkCompareProps {
  prefix: string;
}

const SingleBenchmarkCompare: React.FC<SingleBenchmarkCompareProps> = ({
  prefix,
}) => {
  const { getUserEmail } = useAuth();
  const [compareInput, setCompareInput] = useState("");
  const [responseType, setResponseType] = useState("");
  const [compareEndpointResult, setCompareEndpointResult] = useState("");
  const [compareDistance, setCompareDistance] = useState<number | undefined>(
    undefined
  );
  const [isComparing, setIsComparing] = useState(false);

  // Additional state for BenchmarkCompareResponse fields
  const [newReplyNodeUuid, setNewReplyNodeUuid] = useState("");
  const [queryId, setQueryId] = useState("");
  const [text, setText] = useState("");
  const [options, setOptions] = useState<Array<[string, string]>>([]);
  const [question, setQuestion] = useState("");
  const [audio, setAudio] = useState<string | string[]>("");
  const [media, setMedia] = useState<string | null>(null);

  // Additional state for BenchmarkActionResponse fields
  const [action, setAction] = useState("");

  // New state for dropdown options for testing the single compare benchmark
  const [selectedOptions, setSelectedOptions] = useState<
    Array<[string, string]>
  >([]);
  const [selectedLinks, setSelectedLinks] = useState<Array<[string, string]>>(
    []
  );
  const [currentNodeUUID, setCurrentNodeUUID] = useState("");
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [showCompareDropdowns, setShowCompareDropdowns] = useState(false);

  // Text input states for options
  const [optionText, setOptionText] = useState("");
  const [optionUUID, setOptionUUID] = useState("");
  const [actionText, setActionText] = useState("");
  const [linkText, setLinkText] = useState("");
  const [linkUUID, setLinkUUID] = useState("");
  const [chatHistory, setChatHistory] = useState<string[]>([]);
  const [idHistory, setIdHistory] = useState<string[]>([]);
  const [chatHistoryText, setChatHistoryText] = useState("");
  const [idHistoryText, setIdHistoryText] = useState("");

  // New state for JSON string inputs
  const [masterJson, setMasterJson] = useState("");

  // Helper functions for text input management
  const addOption = () => {
    if (optionText.trim() && optionUUID.trim()) {
      setSelectedOptions((prev) => [
        ...prev,
        [optionText.trim(), optionUUID.trim()],
      ]);
      setOptionText("");
      setOptionUUID("");
    }
  };

  const removeOption = (index: number) => {
    setSelectedOptions((prev) => prev.filter((_, i) => i !== index));
  };

  const addLink = () => {
    if (linkText.trim() && linkUUID.trim()) {
      setSelectedLinks((prev) => [...prev, [linkText.trim(), linkUUID.trim()]]);
      setLinkText("");
      setLinkUUID("");
    }
  };

  const removeLink = (index: number) => {
    setSelectedLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const addAction = () => {
    if (actionText.trim()) {
      setSelectedActions((prev) => [...prev, actionText.trim()]);
      setActionText("");
    }
  };

  const removeAction = (index: number) => {
    setSelectedActions((prev) => prev.filter((_, i) => i !== index));
  };

  const addChatHistory = () => {
    if (chatHistoryText.trim()) {
      setChatHistory((prev) => [...prev, chatHistoryText.trim()]);
      setChatHistoryText("");
    }
  };

  const removeChatHistory = (index: number) => {
    setChatHistory((prev) => prev.filter((_, i) => i !== index));
  };

  const addIdHistory = () => {
    if (idHistoryText.trim()) {
      setIdHistory((prev) => [...prev, idHistoryText.trim()]);
      setIdHistoryText("");
    }
  };

  const removeIdHistory = (index: number) => {
    setIdHistory((prev) => prev.filter((_, i) => i !== index));
  };

  // Master JSON parsing function
  const parseMasterJson = () => {
    if (!masterJson.trim()) return;

    try {
      const parsed = JSON.parse(masterJson);

      // Parse user message
      if (parsed.user_message) {
        setCompareInput(parsed.user_message);
      }

      // Parse current node UUID
      if (parsed.current_node_id) {
        setCurrentNodeUUID(parsed.current_node_id);
      }

      // Parse options
      if (parsed.options && Array.isArray(parsed.options)) {
        const optionsArray = parsed.options
          .map((option: any) => {
            if (Array.isArray(option) && option.length >= 2) {
              return [option[0], option[1]]; // [text, uuid]
            }
            return null;
          })
          .filter(Boolean) as Array<[string, string]>;
        setSelectedOptions(optionsArray);
      }

      // Parse links
      if (parsed.links && Array.isArray(parsed.links)) {
        const linksArray = parsed.links
          .map((link: any) => {
            if (Array.isArray(link) && link.length >= 2) {
              return [link[0], link[1]]; // [text, uuid]
            }
            return null;
          })
          .filter(Boolean) as Array<[string, string]>;
        setSelectedLinks(linksArray);
      }

      // Parse actions
      if (parsed.actions && Array.isArray(parsed.actions)) {
        setSelectedActions(parsed.actions);
      }

      // Parse chat history
      if (parsed.chatHistory && Array.isArray(parsed.chatHistory)) {
        const chatHistoryArray = parsed.chatHistory.map((item: any) =>
          typeof item === "string" ? item : JSON.stringify(item)
        );
        setChatHistory(chatHistoryArray);
      }

      // Parse ID history
      if (parsed.idHistory && Array.isArray(parsed.idHistory)) {
        const idHistoryArray = parsed.idHistory.map((item: any) =>
          typeof item === "string" ? item : JSON.stringify(item)
        );
        setIdHistory(idHistoryArray);
      }

      // Clear the master JSON input after successful parsing
      setMasterJson("");
    } catch (error) {
      console.error("Failed to parse master JSON:", error);
    }
  };

  const clearAllSelections = () => {
    setSelectedOptions([]);
    setSelectedLinks([]);
    setSelectedActions([]);
    setCurrentNodeUUID("");
    setOptionText("");
    setOptionUUID("");
    setActionText("");
    setChatHistory([]);
    setIdHistory([]);
    setChatHistoryText("");
    setIdHistoryText("");
    setMasterJson("");
  };

  const handleCompare = async () => {
    setIsComparing(true);

    // Clear all previous results
    setResponseType("");
    setCompareEndpointResult("");
    setCompareDistance(undefined);
    setNewReplyNodeUuid("");
    setQueryId("");
    setText("");
    setOptions([]);
    setQuestion("");
    setAudio("");
    setMedia(null);
    setAction("");

    const params: CompareRequestParams = {
      userMessage: compareInput,
      options: selectedOptions,
      nodeUUID: currentNodeUUID,
      actions: selectedActions,
      links: selectedLinks,
      userEmail: getUserEmail(),
      chatHistory: chatHistory,
      idHistory: idHistory,
      expected_response: currentNodeUUID, // Provide a default value to avoid None error
    };
    try {
      const result = await debuggerService.makeCompareBenchmarkRequest(
        params,
        prefix
      );

      // Handle action response (BenchmarkActionResponse)
      if ("action" in result && result.action) {
        setAction(result.action);
        if (result.endpoint_result) {
          setCompareEndpointResult(result.endpoint_result);
        }
        if (result.distance !== undefined) {
          setCompareDistance(result.distance);
        }
        setResponseType("Action Response");
      }
      // Handle chat message response (BenchmarkCompareResponse)
      else if ("text" in result && result.text) {
        setText(result.text);
        if (result.nodeUuid) {
          setNewReplyNodeUuid(result.nodeUuid);
        }
        if (result.messageId) {
          setQueryId(result.messageId);
        }
        if (result.options) {
          setOptions(result.options);
        }
        if (result.origin) {
          setQuestion(result.origin);
        }
        if (result.audio) {
          setAudio(result.audio);
        }
        if (result.media) {
          setMedia(result.media);
        }
        if (result.endpoint_result) {
          setCompareEndpointResult(result.endpoint_result);
        }
        if (result.distance !== undefined) {
          setCompareDistance(result.distance);
        }
        setResponseType("Chat Message Response");
      } else {
        setResponseType("Unknown Response Type");
      }
    } catch (err) {
      console.error("Compare failed:", err);
      setResponseType("Error occurred");
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 p-1 bg-[#46ACC6]/10 rounded-xl">
      <div className="flex flex-row items-center gap-2 bg-transparent">
        <input
          type="text"
          placeholder="Enter message to compare…"
          value={compareInput}
          onChange={(e) => setCompareInput(e.target.value)}
          className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base border border-[#46ACC6]/40 focus:border-[#46ACC6]/60 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#46ACC6]/20 bg-white/20 backdrop-blur-sm"
        />
        <button
          onClick={() => setShowCompareDropdowns(!showCompareDropdowns)}
          className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm min-w-[100px]"
        >
          {showCompareDropdowns ? "Hide Options" : "Show Options"}
        </button>
        <button
          onClick={handleCompare}
          disabled={!compareInput.trim() || isComparing}
          className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 disabled:cursor-not-allowed border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm min-w-[100px]"
        >
          {isComparing ? (
            <div className="flex items-center justify-center w-full bg-transparent">
              <LoadingSpinner size="sm" className="mr-2 bg-transparent" />
            </div>
          ) : (
            "Compare"
          )}
        </button>
      </div>

      {/* Text Input Options */}
      {showCompareDropdowns && (
        <div className="space-y-3 p-3 bg-white/10 rounded-lg border border-[#46ACC6]/20">
          {/* Master JSON Input */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#46ACC6]">
              Master JSON (Fill All Fields):
            </label>
            <textarea
              value={masterJson}
              onChange={(e) => setMasterJson(e.target.value)}
              placeholder='Paste complete JSON object like: {"user_message": "...", "current_node_id": "...", "options": [...], "chatHistory": [...], "idHistory": [...], ...}'
              className="w-full px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none h-24 resize-y"
            />
            <button
              onClick={parseMasterJson}
              disabled={!masterJson.trim()}
              className="w-full px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
            >
              Parse Master JSON & Fill All Fields
            </button>
          </div>

          {/* Current Node UUID */}
          <div className="space-y-1">
            <label className="text-xs font-medium text-[#46ACC6]">
              Current Node UUID:
            </label>
            <input
              type="text"
              value={currentNodeUUID}
              onChange={(e) => setCurrentNodeUUID(e.target.value)}
              placeholder="Enter node UUID..."
              className="w-full px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
            />
          </div>

          {/* Chat History */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#46ACC6]">
              Chat History:
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatHistoryText}
                  onChange={(e) => setChatHistoryText(e.target.value)}
                  placeholder="Enter chat history message..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <button
                  onClick={addChatHistory}
                  disabled={!chatHistoryText.trim()}
                  className="px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
                >
                  Add
                </button>
              </div>

              {/* Display selected chat history */}
              {chatHistory.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {chatHistory.map((message, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white/10 rounded px-2 py-1"
                    >
                      <span className="text-xs text-gray-700">{message}</span>
                      <button
                        onClick={() => removeChatHistory(index)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ID History */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#46ACC6]">
              ID History:
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={idHistoryText}
                  onChange={(e) => setIdHistoryText(e.target.value)}
                  placeholder="Enter ID history..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <button
                  onClick={addIdHistory}
                  disabled={!idHistoryText.trim()}
                  className="px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
                >
                  Add
                </button>
              </div>

              {/* Display selected ID history */}
              {idHistory.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {idHistory.map((id, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white/10 rounded px-2 py-1"
                    >
                      <span className="text-xs text-gray-700">{id}</span>
                      <button
                        onClick={() => removeIdHistory(index)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#46ACC6]">
              Options (Text & UUID):
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={optionText}
                  onChange={(e) => setOptionText(e.target.value)}
                  placeholder="Option text..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <input
                  type="text"
                  value={optionUUID}
                  onChange={(e) => setOptionUUID(e.target.value)}
                  placeholder="Option UUID..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <button
                  onClick={addOption}
                  disabled={!optionText.trim() || !optionUUID.trim()}
                  className="px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
                >
                  Add
                </button>
              </div>

              {/* Display selected options */}
              {selectedOptions.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedOptions.map((option, index) => {
                    const [text, uuid] = option;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white/10 rounded px-2 py-1"
                      >
                        <span className="text-xs text-gray-700">
                          {text} ({uuid})
                        </span>
                        <button
                          onClick={() => removeOption(index)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Link Text */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#46ACC6]">
              Links (Text & UUID):
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Link text..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <input
                  type="text"
                  value={linkUUID}
                  onChange={(e) => setLinkUUID(e.target.value)}
                  placeholder="Link UUID..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <button
                  onClick={addLink}
                  disabled={!linkText.trim() || !linkUUID.trim()}
                  className="px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
                >
                  Add
                </button>
              </div>
              {/* Display selected links */}
              {selectedLinks.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedLinks.map((link, index) => {
                    const [text, uuid] = link;
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-white/10 rounded px-2 py-1"
                      >
                        <span className="text-xs text-gray-700">
                          {text} ({uuid})
                        </span>
                        <button
                          onClick={() => removeLink(index)}
                          className="text-xs text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-[#46ACC6]">
              Actions:
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={actionText}
                  onChange={(e) => setActionText(e.target.value)}
                  placeholder="Enter action..."
                  className="flex-1 px-2 py-1 text-xs border border-[#46ACC6]/40 rounded-md bg-white/20 backdrop-blur-sm focus:border-[#46ACC6]/60 focus:outline-none"
                />
                <button
                  onClick={addAction}
                  disabled={!actionText.trim()}
                  className="px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 disabled:bg-gray-400/20 disabled:text-gray-400 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
                >
                  Add
                </button>
              </div>

              {/* Display selected actions */}
              {selectedActions.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {selectedActions.map((action, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white/10 rounded px-2 py-1"
                    >
                      <span className="text-xs text-gray-700">{action}</span>
                      <button
                        onClick={() => removeAction(index)}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Clear All Button */}
          <button
            onClick={clearAllSelections}
            className="w-full px-2 py-1 text-xs text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300"
          >
            Clear All Selections
          </button>
        </div>
      )}

      {/* Selection Summary */}
      {!showCompareDropdowns &&
        (selectedOptions.length > 0 ||
          selectedActions.length > 0 ||
          currentNodeUUID ||
          chatHistory.length > 0 ||
          idHistory.length > 0) && (
          <div className="bg-white/10 rounded-lg p-2 border border-[#46ACC6]/20">
            <div className="text-xs text-[#46ACC6] font-medium mb-1">
              Selected:
            </div>
            {currentNodeUUID && (
              <div className="text-xs text-gray-700 mb-1">
                Node: {currentNodeUUID}
              </div>
            )}
            {chatHistory.length > 0 && (
              <div className="text-xs text-gray-700 mb-1">
                Chat History: {chatHistory.length} message(s)
                {masterJson && (
                  <span className="text-[#46ACC6] ml-1">(from JSON)</span>
                )}
              </div>
            )}
            {idHistory.length > 0 && (
              <div className="text-xs text-gray-700 mb-1">
                ID History: {idHistory.length} ID(s)
                {masterJson && (
                  <span className="text-[#46ACC6] ml-1">(from JSON)</span>
                )}
              </div>
            )}
            {selectedOptions.length > 0 && (
              <div className="text-xs text-gray-700 mb-1">
                Options: {selectedOptions.map(([text]) => text).join(", ")}
              </div>
            )}
            {selectedActions.length > 0 && (
              <div className="text-xs text-gray-700">
                Actions: {selectedActions.join(", ")}
              </div>
            )}
          </div>
        )}

      {/* Results */}
      {(responseType ||
        compareEndpointResult ||
        compareDistance !== undefined ||
        newReplyNodeUuid ||
        queryId ||
        text ||
        options.length > 0 ||
        question ||
        audio ||
        media ||
        action) && (
        <div className="mt-4 p-3 bg-white/10 rounded-lg border border-[#46ACC6]/20">
          <h4 className="text-sm font-medium text-[#46ACC6] mb-2">
            Result of '/benchmark/compare':
          </h4>

          {/* Common fields */}
          {responseType && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                Response type:
              </span>
              <span className="ml-2 text-sm text-gray-800">{responseType}</span>
            </div>
          )}
          {compareEndpointResult && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                Endpoint Result:
              </span>
              <span className="ml-2 text-sm text-gray-800">
                {compareEndpointResult}
              </span>
            </div>
          )}
          {compareDistance !== undefined && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                Distance:
              </span>
              <span className="ml-2 text-sm text-gray-800">
                {compareDistance}
              </span>
            </div>
          )}

          {/* BenchmarkCompareResponse fields */}
          {newReplyNodeUuid && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                New Reply Node UUID:
              </span>
              <span className="ml-2 text-sm text-gray-800">
                {newReplyNodeUuid}
              </span>
            </div>
          )}
          {queryId && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                Query ID:
              </span>
              <span className="ml-2 text-sm text-gray-800">{queryId}</span>
            </div>
          )}
          {text && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">Text:</span>
              <span className="ml-2 text-sm text-gray-800">{text}</span>
            </div>
          )}
          {question && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                Question:
              </span>
              <span className="ml-2 text-sm text-gray-800">{question}</span>
            </div>
          )}
          {audio && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">Audio:</span>
              <span className="ml-2 text-sm text-gray-800">
                {Array.isArray(audio) ? audio.join(", ") : audio}
              </span>
            </div>
          )}
          {media && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">Media:</span>
              <span className="ml-2 text-sm text-gray-800">{media}</span>
            </div>
          )}
          {options.length > 0 && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">
                Options:
              </span>
              <div className="ml-2 mt-1 space-y-1">
                {options.map((option, index) => {
                  const [optionText, optionUuid] = option;
                  return (
                    <div key={index} className="text-sm text-gray-800">
                      {optionText} ({optionUuid})
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* BenchmarkActionResponse fields */}
          {action && (
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600">Action:</span>
              <span className="ml-2 text-sm text-gray-800">{action}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SingleBenchmarkCompare;
